import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    Box, 
    Grid, 
    Button, 
    Text, 
    VStack, 
    HStack, 
    Flex,
    Badge,
    Heading,
    Container,
    useColorModeValue,
    Tooltip,
    Icon
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';
import { FaCoins, FaInfoCircle, FaLock } from 'react-icons/fa';
import { useGame } from '../../context/GameContext';

// Simulated VRF (Verifiable Random Function) for client-side
const simulateVRF = (seed) => {
    // In a real implementation, this would call a blockchain VRF
    // For now, we'll simulate it with a deterministic but unpredictable function
    
    // Create a simple hash from the seed + current timestamp
    const timestamp = Date.now().toString();
    const input = `${seed}:${timestamp}`;
    
    // Use browser's crypto API to generate random values
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    
    // Combine with our input for more entropy
    const randomValue = array[0] / (0xffffffff) + Math.random();
    
    // Return a value between 0 and 1
    return randomValue;
};

const GiftBoxGame = () => {
    const [gifts, setGifts] = useState([]);
    const [picksRemaining, setPicksRemaining] = useState(3);
    const [selectedGifts, setSelectedGifts] = useState([]);
    const [revealedGifts, setRevealedGifts] = useState([]);
    const [totalWinAmount, setTotalWinAmount] = useState(0);
    const [isRevealing, setIsRevealing] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [gameId, setGameId] = useState('');
    const [vrfProofs, setVrfProofs] = useState([]);
    const giftRef = useRef(null);
    
    const { gameState } = useGame();
    const { betAmount = 1 } = gameState;
    
    // Higher multipliers for the gifts (x5-x100)
    const multipliers = [5, 10, 15, 20, 25, 30, 40, 50, 100];
    
    // Generate a unique game ID when the game starts
    const generateGameId = () => {
        return Math.random().toString(36).substring(2, 15) + 
               Math.random().toString(36).substring(2, 15);
    };
    
    // Initialize gifts with random multipliers using simulated VRF
    useEffect(() => {
        if (gameStarted && !gifts.length) {
            const newGameId = generateGameId();
            setGameId(newGameId);
            
            // Generate VRF proofs for each gift
            const proofs = [];
            const newGifts = [];
            
            for (let i = 0; i < 9; i++) {
                const seed = `${newGameId}:gift:${i}`;
                const vrfValue = simulateVRF(seed);
                proofs.push(vrfValue);
                
                // Determine multiplier based on VRF value
                // Higher multipliers have lower probability
                let multiplier;
                if (vrfValue < 0.40) { // 40% chance for lowest multipliers
                    multiplier = multipliers[0]; // 5x
                } else if (vrfValue < 0.65) { // 25% chance
                    multiplier = multipliers[1]; // 10x
                } else if (vrfValue < 0.80) { // 15% chance
                    multiplier = multipliers[2]; // 15x
                } else if (vrfValue < 0.90) { // 10% chance
                    multiplier = multipliers[3]; // 20x
                } else if (vrfValue < 0.95) { // 5% chance
                    multiplier = multipliers[4]; // 25x
                } else if (vrfValue < 0.97) { // 2% chance
                    multiplier = multipliers[5]; // 30x
                } else if (vrfValue < 0.985) { // 1.5% chance
                    multiplier = multipliers[6]; // 40x
                } else if (vrfValue < 0.995) { // 1% chance
                    multiplier = multipliers[7]; // 50x
                } else { // 0.5% chance
                    multiplier = multipliers[8]; // 100x
                }
                
                newGifts.push({
                    id: i,
                    multiplier,
                    revealed: false,
                    vrfValue
                });
            }
            
            setGifts(newGifts);
            setVrfProofs(proofs);
        }
    }, [gameStarted]);
    
    const launchConfetti = useCallback((element) => {
        // Check if element exists before accessing getBoundingClientRect
        if (!element) return;
        
        try {
            const rect = element.getBoundingClientRect();
            // Check if rect has the required properties
            if (!rect) return;
            
            const x = (rect.left + rect.width / 2) / window.innerWidth; // center of the gift
            const y = rect.top / window.innerHeight; // top of the gift

            import('canvas-confetti').then(({ default: confetti }) => {
                confetti({
                    origin: { x, y },
                    particleCount: 200,
                    spread: 80,
                    startVelocity: 30,
                    gravity: 1,
                    ticks: 200,
                    zIndex: 9999,
                    colors: [
                        '#FFD700',  // gold
                        '#FFA500',  // orange
                        '#FF0000',  // red
                        '#800080',  // purple
                        '#FFFFFF',  // white
                    ]
                });
            }).catch(err => {
                console.error("Error loading confetti:", err);
            });
        } catch (error) {
            console.error("Error in launchConfetti:", error);
        }
    }, []);
    
    const handleGiftSelect = (giftId) => {
        // Don't allow selection if already selected or revealing
        if (selectedGifts.includes(giftId) || isRevealing || picksRemaining <= 0) return;
        
        setSelectedGifts(prev => [...prev, giftId]);
        setIsRevealing(true);
        setPicksRemaining(prev => prev - 1);
        
        // Simulate opening animation
        setTimeout(() => {
            const gift = gifts.find(g => g.id === giftId);
            setRevealedGifts(prev => [...prev, gift]);
            
            const win = gift.multiplier * betAmount;
            setTotalWinAmount(prev => prev + win);
            
            // Launch confetti
            if (giftRef.current) {
                launchConfetti(giftRef.current);
            }
            
            setIsRevealing(false);
        }, 1500);
    };
    
    const startGame = () => {
        setGameStarted(true);
    };
    
    const resetGame = () => {
        // Reset the game state
        setGifts([]);
        setPicksRemaining(3);
        setSelectedGifts([]);
        setRevealedGifts([]);
        setTotalWinAmount(0);
        setIsRevealing(false);
        setGameStarted(false);
        setGameId('');
        setVrfProofs([]);
    };
    
    const glowAnimation = keyframes`
      0% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
      50% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.8); }
      100% { box-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
    `;
    
    return (
        <Container maxW="1200px" py={6}>
            <Heading 
                fontSize="3xl" 
                color="purple.300" 
                textAlign="center" 
                mb={6}
                fontFamily="Aclonica, sans-serif"
                textShadow="0 0 10px rgba(128, 90, 213, 0.6)"
            >
                Gift Box Frenzy: Unwrap the Mystery
            </Heading>
            
            <Flex direction={{ base: "column", lg: "row" }} gap={6} align="stretch">
                {/* Left Side - Game Rules */}
                <Box 
                    flex="1" 
                    bg="whiteAlpha.100" 
                    p={4} 
                    borderRadius="lg"
                    boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                >
                    <Heading size="md" color="yellow.300" mb={4} textAlign="center">
                        Game Rules
                    </Heading>
                    <VStack spacing={4} align="stretch">
                        <Box bg="whiteAlpha.200" p={3} borderRadius="md">
                            <Text color="white" fontWeight="bold" mb={1}>
                                How to Play:
                            </Text>
                            <Text color="gray.300" fontSize="sm">
                                Unwrap 3 mysterious gift boxes to reveal surprising multipliers between x5 and x100, and win big!
                            </Text>
                        </Box>
                        
                        <Box bg="whiteAlpha.200" p={3} borderRadius="md">
                            <Text color="white" fontWeight="bold" mb={1}>
                                Winnings:
                            </Text>
                            <Text color="gray.300" fontSize="sm">
                                Each multiplier is applied to your bet amount. All 3 picks are added together for your total win!
                            </Text>
                        </Box>
                        
                        <Box bg="purple.900" p={3} borderRadius="md">
                            <Flex align="center" justify="center">
                                <Text color="yellow.300" fontWeight="bold" textAlign="center" mr={2}>
                                    Secured by Solrand VRF
                                </Text>
                                <Tooltip label="Verifiable Random Function ensures fair and transparent results">
                                    <span><FaInfoCircle color="#CBD5E0" /></span>
                                </Tooltip>
                            </Flex>
                        </Box>
                    </VStack>
                </Box>
                
                {/* Center - Game Grid */}
                <Box 
                    flex="2"
                    bg="gray.800" 
                    p={5} 
                    borderRadius="lg"
                    boxShadow="0 4px 20px rgba(0,0,0,0.4)"
                    position="relative"
                >
                    {!gameStarted ? (
                        <VStack spacing={6} justify="center" h="100%" p={6}>
                            <Heading size="md" color="yellow.300" textAlign="center">
                                Ready to Unwrap the Mystery?
                            </Heading>
                            <Text color="gray.300" textAlign="center">
                                Open 3 gift boxes to reveal multipliers between x5 and x100!
                            </Text>
                            <Button 
                                colorScheme="purple" 
                                size="lg" 
                                onClick={startGame}
                                px={8}
                                py={6}
                                fontSize="xl"
                                _hover={{
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 6px 20px rgba(128, 90, 213, 0.4)"
                                }}
                                transition="all 0.3s"
                            >
                                Start Game
                            </Button>
                        </VStack>
                    ) : (
                        <>
                            <HStack spacing={4} justify="space-between" mb={4}>
                                <Badge colorScheme="purple" fontSize="md" p={2} borderRadius="md">
                                    Picks Remaining: {picksRemaining}
                                </Badge>
                                
                                <Badge colorScheme="green" fontSize="md" p={2} borderRadius="md">
                                    Total Win: {totalWinAmount.toFixed(2)} JINU
                                </Badge>
                            </HStack>
                            
                            <Grid 
                                templateColumns="repeat(3, 1fr)" 
                                gap={4} 
                                mb={6}
                            >
                                {gifts.map((gift) => {
                                    const isSelected = selectedGifts.includes(gift.id);
                                    const isRevealed = revealedGifts.some(g => g.id === gift.id);
                                    
                                    return (
                                        <Box
                                            key={gift.id}
                                            bg={isRevealed ? "yellow.400" : "purple.700"}
                                            p={4}
                                            borderRadius="md"
                                            cursor={!isSelected && !isRevealing && picksRemaining > 0 ? "pointer" : "default"}
                                            onClick={() => handleGiftSelect(gift.id)}
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            height="120px"
                                            boxShadow="0 4px 8px rgba(0,0,0,0.2)"
                                            _hover={{
                                                bg: !isSelected && !isRevealing && picksRemaining > 0 ? "purple.600" : undefined,
                                                transform: !isSelected && !isRevealing && picksRemaining > 0 ? "translateY(-5px)" : undefined
                                            }}
                                            animation={isSelected && !isRevealed ? 
                                                `${glowAnimation} 2s infinite` : "none"}
                                            transition="all 0.3s"
                                            ref={isSelected && !isRevealed ? giftRef : null}
                                        >
                                            {isRevealed ? (
                                                <VStack spacing={1}>
                                                    <Text fontSize="2xl" fontWeight="bold" color="purple.900">
                                                        x{gift.multiplier}
                                                    </Text>
                                                    <FaCoins size="30px" color="gold" />
                                                </VStack>
                                            ) : (
                                                <Box 
                                                    as={motion.div}
                                                    animate={
                                                        isSelected && isRevealing ? 
                                                        { rotateY: 180, scale: [1, 1.1, 1] } : 
                                                        {}
                                                    }
                                                    transition={{ duration: 1.5 }}
                                                    fontSize="5xl"
                                                    position="relative"
                                                >
                                                    🎁
                                                    {picksRemaining <= 0 && !isSelected && (
                                                        <Box 
                                                            position="absolute" 
                                                            top="50%" 
                                                            left="50%" 
                                                            transform="translate(-50%, -50%)"
                                                            color="gray.200"
                                                            fontSize="xl"
                                                        >
                                                            <Icon as={FaLock} />
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Grid>
                            
                            {picksRemaining === 0 && (
                                <Box 
                                    mt={4} 
                                    p={4} 
                                    bg="green.800" 
                                    borderRadius="lg" 
                                    w="100%"
                                    textAlign="center"
                                >
                                    <Text fontSize="lg" color="white" mb={2}>
                                        Total Winnings:
                                    </Text>
                                    <Text fontSize="3xl" fontWeight="bold" color="yellow.300">
                                        {totalWinAmount.toFixed(2)} JINU
                                    </Text>
                                    <HStack spacing={2} justify="center" mt={2}>
                                        {revealedGifts.map((gift, index) => (
                                            <Badge key={index} colorScheme="yellow" p={1}>
                                                x{gift.multiplier}
                                            </Badge>
                                        ))}
                                    </HStack>
                                    
                                    <Button 
                                        mt={4} 
                                        colorScheme="purple" 
                                        onClick={resetGame}
                                    >
                                        Play Again
                                    </Button>
                                    
                                    <Text fontSize="xs" color="gray.300" mt={2}>
                                        Game ID: {gameId.substring(0, 8)}...
                                    </Text>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
                
                {/* Right Side - Multiplier Info */}
                <Box 
                    flex="1" 
                    bg="whiteAlpha.100" 
                    p={4} 
                    borderRadius="lg"
                    boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                >
                    <Heading size="md" color="yellow.300" mb={4} textAlign="center">
                        Possible Multipliers
                    </Heading>
                    <VStack spacing={2}>
                        {multipliers.sort((a, b) => a - b).map((multiplier, idx) => (
                            <Box 
                                key={idx} 
                                bg="purple.900" 
                                p={2} 
                                borderRadius="md"
                                border="1px solid"
                                borderColor="purple.600"
                                w="100%"
                                textAlign="center"
                            >
                                <Text fontWeight="bold" color="yellow.300">x{multiplier}</Text>
                            </Box>
                        ))}
                    </VStack>
                    
                    <Box mt={6} p={3} bg="whiteAlpha.200" borderRadius="md">
                        <Text color="gray.300" fontSize="sm" textAlign="center">
                            Each gift contains one of these multipliers. Find the x100 for the biggest win!
                        </Text>
                    </Box>
                </Box>
            </Flex>
        </Container>
    );
};

export default GiftBoxGame;
